import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import {
  blogCategoriesQuery,
  authorDeleteMutation,
  authorMutation,
  authorQuery,
  siteQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import AuthorForm from '../components/AuthorForm/AuthorForm.jsx';
import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';

export const Author = () => {
  const { t, i18n } = useTranslation();
  const { authorID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // get author
  const {
    data: authorData,
    isPending: authorIsPending,
    isError,
  } = useQuery(authorQuery(authorID));

  // save author
  const mutation = useMutation(
    authorMutation(authorID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['authors', 'list'] });
        // invalidate the detail for revisits
        queryClient.invalidateQueries({
          queryKey: ['authors', 'detail', authorID],
        });
      },
    }),
  );

  // delete author
  const deleteMutation = useMutation(
    authorDeleteMutation(authorID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['authors', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/sites/${siteID}/blogs/authors`);
      },
    }),
  );

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/blogs`}>Blogs</Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/blogs/authors`}>
                {t('Blog authors')}
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {authorIsPending && <span>...</span>}
              {authorData && <span>{authorData.name}</span>}
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            {authorIsPending && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {authorData && !isError && (
              <>
                <AuthorForm
                  authorData={authorData}
                  mutation={mutation}
                  disabled={mutation.isPending || deleteMutation.isPending}
                  siteID={siteID}
                />
                <div className="sb-message-action-section">
                  <ActionObject
                    onAction={() => deleteMutation.mutate()}
                    loading={deleteMutation.isPending}
                    actionName="Delete"
                    actionVerb="Deleting"
                    label="Delete author"
                    note="This author will be permanently deleted"
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
